import type { ClientContext, NestedClient } from "@orpc/client";
import { createORPCClient } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import * as Sentry from "@sentry/react";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import {
	MutationCache,
	onlineManager,
	QueryCache,
	QueryClient,
} from "@tanstack/react-query";
import { del, get, set } from "idb-keyval";
import { toast } from "sonner";
import { getCsrfToken, invalidateCsrfToken } from "./csrf";

const STATE_CHANGING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
const OFFLINE_ERROR_MESSAGE = "You are offline. Reconnect to sync changes.";
const ONLINE_POLL_INTERVAL_MS = 2000;
let offlineToastShown = false;

const showOfflineToastOnce = (): void => {
	if (offlineToastShown) {
		return;
	}
	offlineToastShown = true;
	toast.error(OFFLINE_ERROR_MESSAGE);
};

export interface ORPCSetupConfig {
	/** API base URL (e.g., import.meta.env.VITE_API_URL) */
	apiUrl: string;
	/** Enable IndexedDB persistence and offline mode. Defaults to false. */
	enableOffline?: boolean;
	/** Enable Sentry error tracking for queries/mutations. Defaults to true. */
	enableSentry?: boolean;
}

// ISO 8601 date regex for deserialization
const ISO_DATE_REGEX =
	/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

// Recursively convert ISO date strings back to Date objects
const reviveDates = (obj: unknown): unknown => {
	if (obj === null || obj === undefined) {
		return obj;
	}
	if (typeof obj === "string") {
		if (ISO_DATE_REGEX.test(obj)) {
			return new Date(obj);
		}
		return obj;
	}
	if (Array.isArray(obj)) {
		return obj.map(reviveDates);
	}
	if (typeof obj === "object") {
		const result: Record<string, unknown> = {};
		for (const [key, value] of Object.entries(obj)) {
			result[key] = reviveDates(value);
		}
		return result;
	}
	return obj;
};

const captureReactQueryError = (
	error: Error,
	context: Record<string, unknown>,
): void => {
	Sentry.captureException(error, {
		contexts: {
			reactQuery: context,
		},
	});
};

/**
 * Check if an error is a network error (fetch failed due to offline/network issues)
 */
const isNetworkError = (error: Error): boolean => {
	const message = error.message.toLowerCase();
	return (
		message.includes("networkerror") ||
		message.includes("network error") ||
		message.includes("failed to fetch") ||
		message.includes("fetch resource") ||
		message.includes("network request failed")
	);
};

/**
 * Detects if a response is a CSRF token error by inspecting the response body.
 */
const detectCsrfError = async (response: Response): Promise<boolean> => {
	try {
		const errorData = await response.clone().json();

		if (
			errorData?.code === "FORBIDDEN" ||
			errorData?.code === "CSRF_TOKEN_INVALID"
		) {
			if (
				errorData.code === "FORBIDDEN" &&
				errorData?.message &&
				typeof errorData.message === "string"
			) {
				try {
					const parsedMessage = JSON.parse(errorData.message);
					if (parsedMessage?.code === "CSRF_TOKEN_INVALID") {
						return true;
					}
				} catch {
					if (errorData.message.includes("CSRF_TOKEN_INVALID")) {
						return true;
					}
				}
			} else if (errorData?.code === "CSRF_TOKEN_INVALID") {
				return true;
			}
		} else if (
			errorData?.message &&
			typeof errorData.message === "string" &&
			errorData.message.includes("CSRF_TOKEN_INVALID")
		) {
			return true;
		}
	} catch {
		try {
			const errorText = await response.clone().text();
			if (
				errorText.includes("CSRF_TOKEN_INVALID") ||
				errorText.includes("CSRF") ||
				errorText.includes("Session ID mismatch")
			) {
				return true;
			}
		} catch {
			// Failed to read error response
		}
	}
	return false;
};

interface ORPCSetupResult<TClient extends NestedClient<ClientContext>> {
	client: TClient;
	orpc: ReturnType<typeof createTanstackQueryUtils<TClient>>;
	queryClient: QueryClient;
	persister: ReturnType<typeof createAsyncStoragePersister> | undefined;
	invalidateCsrfToken: typeof invalidateCsrfToken;
	link: RPCLink<ClientContext>;
}

/**
 * Creates a fully configured oRPC + TanStack Query setup for a Planvera frontend app.
 *
 * @example
 * ```ts
 * import type { AppRouterClient } from "./api-types";
 * const { client, orpc, queryClient, persister, invalidateCsrfToken } =
 *   createORPCSetup<AppRouterClient>({ apiUrl: import.meta.env.VITE_API_URL });
 * ```
 */
export const createORPCSetup = <TClient extends NestedClient<ClientContext>>({
	apiUrl,
	enableOffline = false,
	enableSentry = true,
}: ORPCSetupConfig): ORPCSetupResult<TClient> => {
	// Set up offline mode if enabled
	if (enableOffline && typeof window !== "undefined") {
		onlineManager.setEventListener((setOnline) => {
			const syncNavigatorStatus = (): void => {
				const isOnline =
					typeof navigator !== "undefined" ? navigator.onLine : true;
				setOnline(isOnline);
				if (isOnline) {
					offlineToastShown = false;
				}
			};

			const onlineHandler = (): void => {
				setOnline(true);
				offlineToastShown = false;
			};
			const offlineHandler = (): void => setOnline(false);

			window.addEventListener("online", onlineHandler);
			window.addEventListener("offline", offlineHandler);
			window.addEventListener("visibilitychange", syncNavigatorStatus);

			syncNavigatorStatus();
			const pollId = window.setInterval(
				syncNavigatorStatus,
				ONLINE_POLL_INTERVAL_MS,
			);

			return () => {
				window.removeEventListener("online", onlineHandler);
				window.removeEventListener("offline", offlineHandler);
				window.removeEventListener("visibilitychange", syncNavigatorStatus);
				window.clearInterval(pollId);
			};
		});
	}

	const assertOnline = (): void => {
		if (
			enableOffline &&
			typeof navigator !== "undefined" &&
			!navigator.onLine
		) {
			showOfflineToastOnce();
			throw new Error(OFFLINE_ERROR_MESSAGE);
		}
	};

	const queryClient = new QueryClient({
		queryCache: new QueryCache({
			onError: (error, query) => {
				// Don't log/toast network errors when offline
				if (
					enableOffline &&
					!onlineManager.isOnline() &&
					isNetworkError(error)
				) {
					return;
				}

				if (enableSentry) {
					captureReactQueryError(error, {
						type: "query",
						queryHash: query?.queryHash,
						queryKey: query?.queryKey,
					});
				}

				toast.error(`Error: ${error.message}`, {
					action: {
						label: "retry",
						onClick: () => {
							queryClient.invalidateQueries();
						},
					},
				});
			},
		}),
		mutationCache: new MutationCache({
			onError: (error, _variables, _context, mutation) => {
				if (
					enableOffline &&
					!onlineManager.isOnline() &&
					isNetworkError(error)
				) {
					return;
				}

				if (enableSentry) {
					captureReactQueryError(error, {
						type: "mutation",
						mutationId: mutation?.mutationId,
						mutationKey: mutation?.options.mutationKey,
					});
				}

				if (
					enableOffline &&
					error instanceof Error &&
					error.message === OFFLINE_ERROR_MESSAGE
				) {
					showOfflineToastOnce();
				}
			},
		}),
		defaultOptions: {
			queries: enableOffline
				? {
						gcTime: 24 * 60 * 60 * 1000,
						networkMode: "offlineFirst",
					}
				: undefined,
			mutations: enableOffline
				? {
						onMutate: async () => {
							assertOnline();
						},
					}
				: undefined,
		},
	});

	const persister =
		enableOffline && typeof window !== "undefined"
			? createAsyncStoragePersister({
					storage: {
						getItem: (key) => get(key),
						setItem: (key, value) => set(key, value),
						removeItem: (key) => del(key),
					},
					serialize: (data) => JSON.stringify(data),
					deserialize: (data) =>
						reviveDates(JSON.parse(data)) as ReturnType<typeof JSON.parse>,
				})
			: undefined;

	const link = new RPCLink({
		url: `${apiUrl}/rpc`,
		async fetch(request) {
			const headers = new Headers(request.headers);
			const method = request.method.toUpperCase();

			if (STATE_CHANGING_METHODS.has(method)) {
				const token = await getCsrfToken(apiUrl);
				headers.set("x-csrf-token", token);
			}

			const nextRequest = new Request(request, {
				headers,
				credentials: "include",
			});

			const response = await fetch(nextRequest);

			// If we get a CSRF error, invalidate the token and retry once
			if (
				(response.status === 403 || response.status === 500) &&
				STATE_CHANGING_METHODS.has(method)
			) {
				const isCsrfError = await detectCsrfError(response);

				if (isCsrfError) {
					const newToken = await getCsrfToken(apiUrl, true);
					const retryHeaders = new Headers(request.headers);
					retryHeaders.set("x-csrf-token", newToken);

					const retryRequest = new Request(request, {
						headers: retryHeaders,
						credentials: "include",
					});

					return fetch(retryRequest);
				}
			}

			return response;
		},
	});

	const client = createORPCClient<TClient>(link);
	const orpc = createTanstackQueryUtils(client);

	return { client, orpc, queryClient, persister, invalidateCsrfToken, link };
};
